// Primitives visuelles partagées de l'écran de séance — thème, icônes, badges et chronos.
//
// Extraites de `SessionPage.tsx` pour que le parcours de test de force
// (`StrengthTestBlock`) s'affiche avec exactement les mêmes tuiles, les mêmes couleurs et
// les mêmes chronos que le reste de la séance. Les dupliquer aurait fait diverger les
// deux écrans au premier ajustement de palette.
//
// Rien d'autre n'a bougé : le contenu de chaque composant est repris tel quel.

import { useState, useEffect, useRef } from "react"
import { loadTimerState, saveTimerState, removeTimerState } from "@/lib/sessionResume"

// ═══ THEME ═════════════════════════════════════════════════
export const T = {
  bg:"#060a13",sf:"rgba(12,20,35,0.75)",bd:"rgba(30,41,59,0.55)",bdL:"rgba(30,41,59,0.3)",
  cy:"#06b6d4",cyD:"rgba(6,182,212,0.1)",gn:"#22c55e",or:"#f97316",vi:"#8b5cf6",
  t1:"#e2e8f0",t2:"#94a3b8",t3:"#64748b",
  // Jetons de la maquette de séance v8. Ajoutés à côté des précédents plutôt qu'à leur
  // place : `StrengthTestBlock` et l'accueil de séance s'appuient encore sur ceux-ci,
  // et les faire bouger déplacerait des écrans qui ne sont pas dans ce chantier.
  s1:"#0b1220",s2:"#111a2b",
  line:"rgba(148,163,184,0.14)",line2:"rgba(148,163,184,0.26)",
  txHi:"#f1f5f9",
  // Une couleur d'accent par tuile de paramètre — c'est ce qui les rend lisibles d'un
  // coup d'œil, avant même de lire les libellés.
  cyanBright:"#22d3ee",pink:"#f472b6",green:"#4ade80",violet:"#a78bfa",
  font:"'Barlow',system-ui,-apple-system,sans-serif",
}
export const CSS = `@keyframes fadeUp{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:translateY(0)}}@keyframes pulse{0%,100%{box-shadow:0 0 0 0 rgba(6,182,212,0.3)}50%{box-shadow:0 0 0 10px rgba(6,182,212,0)}}@keyframes spin{to{transform:rotate(360deg)}}*{box-sizing:border-box;margin:0;padding:0}::-webkit-scrollbar{width:0}`

// ═══ ICONS ═════════════════════════════════════════════════
export const I = {
  back:<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5"/><path d="m12 19-7-7 7-7"/></svg>,
  chev:<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6"/></svg>,
  chk:<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>,
  play:<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>,
  pause:<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16" rx="1"/><rect x="14" y="4" width="4" height="16" rx="1"/></svg>,
  playSm:<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><polygon points="6 3 20 12 6 21 6 3"/></svg>,
  clock:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
  fire:<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" opacity="0.85"><path d="M12 2c.5 2.5 2.5 4.5 3 7 .3 1.5-.5 3-2 4-.5-1.5-1-2.5-2-3.5-1 2-2 4.5-2 7 0 3.5 2.5 5.5 5 5.5s5-2 5-5.5c0-4-3-7-5-10-1 1.5-1.5 2.5-2 4z"/></svg>,
  db:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 5v14M18 5v14M3 7v10M21 7v10M6 12h12M3 10h3M18 10h3M3 14h3M18 14h3"/></svg>,
  loop:<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21.5 2v6h-6"/><path d="M21.34 15.57a10 10 0 1 1-.57-8.38L21.5 8"/></svg>,
  film:<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polygon points="10 8 16 12 10 16 10 8" fill="currentColor" stroke="none"/></svg>,
  filmLg:<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polygon points="10 8 16 12 10 16 10 8" fill="currentColor" stroke="none"/></svg>,
  exp:<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></svg>,
  x:<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  redo:<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/></svg>,
  inf:<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>,
  bolt:<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M13 2 4 14h7l-1 8 9-12h-7l1-8z"/></svg>,
  timer2:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="13" r="8"/><path d="M12 9v4l2 2"/><path d="M5 3L2 6"/><path d="M22 6l-3-3"/><path d="M12 5V3"/><path d="M10 3h4"/></svg>,
}

// ═══ CHRONOS & BADGE ═══════════════════════════════════════

// Persistance du chrono plein écran — même technique que `RestTimer` un peu plus haut
// (voir son commentaire) : deadline ABSOLUE (Date.now() + durée) écrite en localStorage
// (lib/sessionResume — sessionStorage ne survit pas à la fermeture de l'app installée par
// iOS) sous une clé stable, recalculée à chaque tick plutôt que décomptée. Sans ça, un
// décompte par tick (`setRem(r => r - 1)` toutes les 1000 ms) reparlait de zéro à chaque
// démontage/remontage réel du composant — et iOS décharge la page au verrouillage
// d'écran ou en arrière-plan prolongé, ce qui démonte réellement `TimerCountdown` (pas
// seulement un throttling d'onglet). C'est très exactement le bug signalé sur le repos
// entre les séries du test de force : l'écran de test réutilisait ce composant, qui
// n'avait jamais reçu le correctif appliqué à `RestTimer` en S23-1 bis pour les séances
// normales. `TimerButton` (ci-dessous) a la même faiblesse théorique mais n'est jamais
// utilisé pour un repos qui conditionne la suite du parcours (bouton démarré à la main,
// sans `onDone`) — laissé tel quel, hors périmètre de ce correctif.
type SavedCountdownState = { sec: number; deadline: number }

function loadCountdownState(key: string): SavedCountdownState | null {
  return loadTimerState<SavedCountdownState>(key)
}
function saveCountdownState(key: string, state: SavedCountdownState) {
  saveTimerState(key, state)
}

/**
 * À appeler au moment où un repos DÉMARRE (clic « Série terminée », résultat enregistré,
 * fin de round) — jamais au montage du chrono.
 *
 * La clé d'un repos (`<séance>|test-<exercice>-<série>`) peut resservir (même séance
 * refaite après un changement de programme), et le stockage la garde jusqu'à 12 h. Une
 * entrée laissée par un test précédent porte une deadline dépassée depuis longtemps : relue telle quelle au montage,
 * elle donnait un chrono à 0:00 qui appelait `onDone` aussitôt — le repos « flashait » et
 * l'écran passait à la suite sans attendre. Le chrono, lui, ne peut pas distinguer cette
 * entrée périmée d'un vrai retour après un repos écoulé en arrière-plan : seul l'appelant
 * sait s'il DÉMARRE un repos ou s'il en RESTAURE un après rechargement de la page. D'où
 * cet effacement explicite côté appelant, et rien d'automatique à l'hydratation.
 */
export function clearCountdown(restKey: string) {
  removeTimerState(`countdown:${restKey}`)
}

/**
 * `restKey` doit être stable pour UN repos donné (ex. `<séance>|interround-${rnd}` pour un
 * repos inter-rounds, `<séance>|test-${exerciseIndex}-${setIndex}` pour un repos entre
 * séries de test), commencer par l'identifiant de la séance suivi de `|` (c'est ce qui
 * permet de l'effacer en fin de séance), et différer d'un repos à l'autre — c'est cette clé, pas le cycle de vie du composant, qui
 * permet de retrouver la deadline après un démontage/remontage réel de la page.
 */
export function TimerCountdown({ sec, onDone, label, restKey }: { sec: number; onDone: () => void; label?: string; restKey: string }) {
  const storageKey = `countdown:${restKey}`
  const [rem, setRem] = useState(sec)
  const deadlineRef = useRef<number | null>(null)
  const doneFired = useRef(false)

  // Hydratation : même clé + même durée retrouvées en storage → on reprend la deadline
  // sauvegardée (correct même si elle est déjà dépassée : `left` retombe à 0) ; sinon
  // (premier montage, ou clé réutilisée pour un repos de durée différente) on pose une
  // deadline fraîche.
  useEffect(() => {
    doneFired.current = false
    const saved = loadCountdownState(storageKey)
    if (saved && saved.sec === sec) {
      deadlineRef.current = saved.deadline
      setRem(Math.max(0, Math.ceil((saved.deadline - Date.now()) / 1000)))
      return
    }
    const deadline = Date.now() + sec * 1000
    deadlineRef.current = deadline
    setRem(sec)
    saveCountdownState(storageKey, { sec, deadline })
  }, [storageKey, sec])

  // Recalcule depuis la deadline absolue à chaque tick plutôt que de décrémenter — ainsi
  // un retour au premier plan après suspension retombe juste, quelle que soit la durée
  // de l'absence.
  useEffect(() => {
    const iv = setInterval(() => {
      if (deadlineRef.current == null) return
      setRem(Math.max(0, Math.ceil((deadlineRef.current - Date.now()) / 1000)))
    }, 250)
    return () => clearInterval(iv)
  }, [storageKey, sec])

  // `onDone` déclenché une seule fois dès que le compte tombe à 0, quel que soit le
  // chemin qui l'y a amené (tick normal ou resynchronisation au retour au premier plan).
  // L'entrée est retirée à la fin du repos (échéance ou « Passer ») : elle n'a plus rien à
  // restaurer, et la laisser est ce qui la faisait relire par le repos suivant de même clé.
  const finish = () => {
    doneFired.current = true
    clearCountdown(restKey)
    onDone()
  }
  useEffect(() => {
    if (rem > 0 || doneFired.current) return
    finish()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rem, onDone])

  // Retour au premier plan : resync immédiate, le setInterval ayant pu être suspendu
  // pendant l'absence (throttling agressif en arrière-plan sur mobile).
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== "visible" || deadlineRef.current == null) return
      setRem(Math.max(0, Math.ceil((deadlineRef.current - Date.now()) / 1000)))
    }
    document.addEventListener("visibilitychange", onVisible)
    return () => document.removeEventListener("visibilitychange", onVisible)
  }, [])

  const relaunch = () => {
    doneFired.current = false
    const deadline = Date.now() + sec * 1000
    deadlineRef.current = deadline
    setRem(sec)
    saveCountdownState(storageKey, { sec, deadline })
  }

  const w = rem <= 10
  return (
    <div style={{ background:T.sf,border:`1px solid ${T.bd}`,borderRadius:20,padding:"32px 24px",textAlign:"center" as const,backdropFilter:"blur(12px)" }}>
      {label && <p style={{ fontSize:13,fontWeight:600,color:T.cy,marginBottom:16 }}>{label}</p>}
      <div style={{ display:"flex",alignItems:"center",justifyContent:"center",gap:8,marginBottom:20 }}><div style={{ color:w ? T.or : T.cy }}>{I.clock}</div><span style={{ color:T.t2,fontSize:11,fontWeight:600,letterSpacing:1.5,textTransform:"uppercase" as const }}>Repos</span></div>
      <div style={{ fontSize:60,fontWeight:700,color:w ? T.or : "#fff",fontVariantNumeric:"tabular-nums" as const,lineHeight:1,marginBottom:20,transition:"color 0.3s",fontFamily:"'SF Mono',ui-monospace,monospace" }}>{Math.floor(rem / 60)}:{(rem % 60).toString().padStart(2,"0")}</div>
      <div style={{ height:3,background:"rgba(255,255,255,0.05)",borderRadius:2,overflow:"hidden" as const,marginBottom:20,marginInline:20 }}><div style={{ height:"100%",width:`${((sec - rem) / sec) * 100}%`,background:w ? T.or : T.cy,borderRadius:2,transition:"width 1s linear" }} /></div>
      <div style={{ display:"flex",gap:12,justifyContent:"center" }}>
        <button onClick={relaunch} style={{ background:"rgba(255,255,255,0.05)",border:`1px solid ${T.bd}`,borderRadius:12,padding:"11px 20px",color:T.t2,fontSize:13,display:"flex",alignItems:"center",gap:6,cursor:"pointer" }}>{I.redo} Relancer</button>
        <button onClick={finish} style={{ background:`linear-gradient(135deg,#0e7490,${T.cy})`,border:"none",borderRadius:12,padding:"11px 28px",color:"#fff",fontSize:13,fontWeight:600,cursor:"pointer" }}>Passer →</button>
      </div>
    </div>
  )
}

export function TimerButton({ label, sec, accent = T.cy }: { label: string; sec: number; accent?: string }) {
  const [running, setRunning] = useState(false)
  const [rem, setRem] = useState(sec)
  useEffect(() => { if (!running || rem <= 0) { if (rem <= 0) { setRunning(false); setRem(sec) }; return }; const t = setTimeout(() => setRem(r => r - 1), 1000); return () => clearTimeout(t) }, [rem, running, sec])
  if (!running) return <button onClick={() => { setRem(sec); setRunning(true) }} style={{ width:"100%",display:"flex",alignItems:"center",justifyContent:"center",gap:10,background:T.sf,border:`1px solid ${T.bd}`,borderRadius:14,padding:"14px 20px",cursor:"pointer" }}><div style={{ color:accent }}>{I.timer2}</div><span style={{ fontSize:14,fontWeight:600,color:T.t1 }}>{label} — {Math.floor(sec / 60)}:{(sec % 60).toString().padStart(2,"0")}</span></button>
  const w = rem <= 10
  return <div style={{ background:T.sf,border:`1px solid ${accent}30`,borderRadius:14,padding:"16px 20px",textAlign:"center" as const }}><div style={{ fontSize:36,fontWeight:700,color:w ? T.or : "#fff",fontVariantNumeric:"tabular-nums" as const,marginBottom:10,fontFamily:"'SF Mono',ui-monospace,monospace" }}>{Math.floor(rem / 60)}:{(rem % 60).toString().padStart(2,"0")}</div><div style={{ height:2,background:"rgba(255,255,255,0.05)",borderRadius:2,overflow:"hidden" as const }}><div style={{ height:"100%",width:`${((sec - rem) / sec) * 100}%`,background:w ? T.or : accent,borderRadius:2,transition:"width 1s linear" }} /></div></div>
}

export const Badge = ({ text }: { text: string }) => <span style={{ padding:"3px 10px",background:"rgba(255,255,255,0.03)",border:`1px solid ${T.bdL}`,borderRadius:6,fontSize:11,color:T.t3,fontWeight:500 }}>{text}</span>
